import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { definirRenovadorDeSessao, setAccessToken } from '@/lib/auth-token';
import {
  logout as logoutRequest,
  switchOrganization as switchOrganizationRequest,
  type AuthenticatedPayload,
} from './api/auth';
import { AuthContext, type AuthContextValue, type AuthState } from './auth-context';
import { refreshSessionOnce } from './refresh-once';

const SIGNED_OUT: AuthState = {
  user: null,
  organization: null,
  organizations: [],
  permissions: [],
  isPlatform: false,
  isRestoring: false,
};

function stateFrom(payload: AuthenticatedPayload): AuthState {
  return {
    user: payload.user,
    organization: payload.organization,
    organizations: payload.organizations,
    permissions: payload.permissions,
    isPlatform: payload.platformRole !== null,
    isRestoring: false,
  };
}

/**
 * Estado de sessão do cliente. O access token não fica no estado do React: vive no módulo
 * `auth-token`, fora da árvore — guardá-lo em estado faria a credencial aparecer em
 * ferramenta de inspeção de componentes.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({ ...SIGNED_OUT, isRestoring: true });

  // Empresa da sessão em uso: se a renovação devolver outra (troca feita em outra aba), o cache
  // desta aba é da empresa anterior e não pode aparecer com o nome e as permissões da nova.
  const organizationRef = useRef<string | null>(null);
  const applySession = useCallback(
    (payload: AuthenticatedPayload) => {
      const next = payload.organization?.id ?? null;
      if (organizationRef.current !== null && organizationRef.current !== next) queryClient.clear();
      organizationRef.current = next;
      setAccessToken(payload.accessToken);
      setState(stateFrom(payload));
    },
    [queryClient],
  );

  const signOutLocally = useCallback(() => {
    organizationRef.current = null;
    setAccessToken(null);
    setState(SIGNED_OUT);
    // Dado de uma empresa não pode sobreviver no cache para a próxima pessoa ou empresa.
    queryClient.clear();
  }, [queryClient]);

  /*
   * O cliente HTTP chama este renovador quando um 401 chega com a aba aberta. Quem atende é
   * o provedor porque só aqui há como derrubar a sessão quando a renovação falha de verdade
   * — sem isso, a tela ficaria respondendo 401 em tudo em vez de voltar ao login.
   */
  useEffect(() => {
    definirRenovadorDeSessao(async () => {
      try {
        const payload = await refreshSessionOnce();
        applySession(payload);
        return payload.accessToken;
      } catch {
        signOutLocally();
        return null;
      }
    });

    return () => definirRenovadorDeSessao(null);
  }, [applySession, signOutLocally]);

  /*
   * Restauração ao carregar a página: o access token em memória se perde no recarregamento,
   * mas o cookie de refresh sobrevive. Falhar aqui é o caso normal de quem não está logado,
   * então não vira erro visível. `refreshSessionOnce` porque o StrictMode monta o efeito
   * duas vezes, e duas rotações simultâneas disparariam a detecção de reuso do servidor.
   */
  useEffect(() => {
    let cancelled = false;

    void refreshSessionOnce()
      .then((payload) => {
        if (!cancelled) applySession(payload);
      })
      .catch(() => {
        if (!cancelled) {
          setAccessToken(null);
          setState(SIGNED_OUT);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [applySession]);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      // Limpa mesmo se a chamada falhar: continuar "logado" na tela depois de pedir para
      // sair é pior que um cookie órfão, que expira sozinho.
      signOutLocally();
    }
  }, [signOutLocally]);

  const switchOrganization = useCallback(
    async (organizationId: string) => {
      const payload = await switchOrganizationRequest(organizationId);
      // Nada do cache da empresa anterior pode aparecer na nova.
      queryClient.clear();
      applySession(payload);
    },
    [applySession, queryClient],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      applySession,
      logout,
      switchOrganization,
      can: (permission: string) => state.permissions.includes(permission),
    }),
    [state, applySession, logout, switchOrganization],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
